import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import FeedbackPopover from './FeedbackPopover';
import { updateCompetitorFeedback } from '../store/slices/competitorSlice';
import { apiService } from '../services/apiService';
import { sessionManager } from '../services/sessionManager';
import { useToast } from '../contexts/ToastContext';
import { ALERT_BADGE_LABEL, COMPETITOR_PALETTE, speakerLabel } from '../constants/alertTheme';
import { AlertShell, AlertBulletList, AlertParagraph, AlertSection } from './alerts/AlertShell';
import EarlierTag from './EarlierTag';
import { useAlertViewer, useIsWatcher } from '../contexts/SessionViewerContext';

const CompetitorAlert = ({ alert, onDismiss }) => {
  const dispatch = useDispatch();
  const { showToast } = useToast();
  // Feedback writes to the owner's session: a watcher only reads the alert.
  const isWatcher = useIsWatcher();
  // "You said" only for the viewer's own mention; anyone else's names its raiser.
  const viewer = useAlertViewer();
  // COLLAPSED BY DEFAULT (owner, 2026-09-13). A competitor mention is
  // context, not a hazard: the badge, the name and the quote are enough to
  // register it in peripheral vision, and the talking points open on a click.
  // The landmine card stays fully expanded — that one needs acting on now.
  const [isExpanded, setIsExpanded] = useState(false);

  const handleFeedbackSubmit = async (rating, reason) => {
    const roomId = sessionManager.getRoomId();

    // Update Redux immediately so UI reflects the choice even if persist fails
    dispatch(updateCompetitorFeedback({
      detected_at: alert.detected_at,
      feedback: { rating, reason: reason || undefined, submittedAt: new Date().toISOString() }
    }));
    showToast('Feedback recorded');

    if (!roomId) return;

    try {
      await apiService.submitEntryFeedback(roomId, {
        entryType: 'competitor',
        entryTimestamp: alert.detected_at,
        rating,
        reason
      });
    } catch (err) {
      console.warn('[CompetitorAlert] Entry feedback persist failed:', err);
    }
  };


  /**
   * ADVANTAGES FIRST, THEN THEIR WEAKNESSES (owner, 2026-09-09).
   *
   * The order is the point. A rep who has just heard a competitor named needs
   * something to SAY, and leading with what we are good at is a better answer
   * than leading with an attack on them — so `advantages` renders first and
   * `weaknesses` second, always, regardless of which array is longer.
   *
   * Removed as noise on the owner's rule: the confidence score, the match type,
   * the timestamp, the "Competitor Analysis" container header with its active
   * count, the copy-to-clipboard buttons, and the collapse that hid all of it
   * behind a click. What is left is what a rep can act on in the two seconds
   * they have.
   *
   * Talking points were removed in that same pass and reinstated on
   * 2026-09-10: a rep needs the recommended line as much as the advantages
   * list, so it now leads the card, directly under the trigger quote, the
   * same way `quick_tips` leads the landmine card.
   */
  const advantages = (alert?.advantages ?? []).filter(Boolean);
  const weaknesses = (alert?.weaknesses ?? []).filter(Boolean);
  const talkingPoints = alert?.talking_points?.trim();
  const asText = (item) => (typeof item === 'string' ? item : item?.point ?? item?.text ?? '');

  return (
    <AlertShell
      palette={COMPETITOR_PALETTE}
      badgeLabel={ALERT_BADGE_LABEL.competitor}
      name={alert?.competitor}
      speaker={speakerLabel(alert, viewer)}
      trigger={alert?.matched_text}
      onDismiss={onDismiss}
      feedback={
        <span className="flex items-center gap-2">
          {alert?.isRestored && <EarlierTag />}
          {!isWatcher && <FeedbackPopover currentFeedback={alert.feedback} onSubmit={handleFeedbackSubmit} />}
        </span>
      }
    >
      <button
        type="button"
        onClick={() => setIsExpanded((open) => !open)}
        aria-expanded={isExpanded}
        className="w-full px-3 py-1.5 text-left text-[11px] font-semibold tracking-[0.04em]"
        style={{ color: COMPETITOR_PALETTE.heading }}
      >
        {isExpanded ? 'Hide details' : 'Show details'}
      </button>
      {isExpanded && talkingPoints && (
        <AlertSection palette={COMPETITOR_PALETTE} title="Talking Points">
          <AlertParagraph palette={COMPETITOR_PALETTE}>{talkingPoints}</AlertParagraph>
        </AlertSection>
      )}

      {isExpanded && advantages.length > 0 && (
        <AlertSection palette={COMPETITOR_PALETTE} title="Our advantages">
          <AlertBulletList
            palette={COMPETITOR_PALETTE}
            items={advantages.map(asText).filter(Boolean)}
          />
        </AlertSection>
      )}

      {isExpanded && weaknesses.length > 0 && (
        <AlertSection palette={COMPETITOR_PALETTE} title={`Where ${alert?.competitor || 'they'} fall short`}>
          <AlertBulletList
            palette={COMPETITOR_PALETTE}
            items={weaknesses.map(asText).filter(Boolean)}
          />
        </AlertSection>
      )}
    </AlertShell>
  );
};

export const CompetitorAlertsContainer = ({ alerts, onDismiss }) => {
  if (!alerts || alerts.length === 0) {
    return null;
  }

  // No header and no count: the badges say what these are, and a live call is
  // not the moment to tell a rep how many things are wrong.
  return (
    <div>
      {alerts.map((alert) => (
        <CompetitorAlert key={alert.id} alert={alert} onDismiss={onDismiss} />
      ))}
    </div>
  );
};

export default CompetitorAlert;